import { useEffect, useState } from 'react'
import { api, type AiManagement as AiData, type AiSeat, type AiSpendRow } from '../lib/api.ts'
import { AccountSpend } from '../components/AccountSpend.tsx'
import { Button } from '../components/Button.tsx'
import { DataTable, type Column } from '../components/DataTable.tsx'
import { Input } from '../components/Input.tsx'
import { SearchableSelect } from '../components/SearchableSelect.tsx'
import { Tag } from '../components/Tag.tsx'
import { icons } from '../components/icons.ts'

/**
 * What the models cost, and which model each step is on.
 *
 * ## Why the two tables are on one screen
 *
 * A step costs what it costs *because* of the model in it. Reading the spend
 * and then going somewhere else to see what produced it is how a number gets
 * read without its cause; here the table above is the answer to the one below.
 *
 * ## Why an override looks like a warning and not like a setting
 *
 * Because it is one. `registry.ts` decides which model holds which step, a
 * reviewed diff is how that changes, and the golden set is what argues for it
 * -- #418 refused a benchmark auto-switch on exactly that ground. What this
 * screen offers is a lever for the case the registry cannot answer fast
 * enough: production stopped reading when a provider ran out of credit, and
 * the only alternative was twenty minutes of CI while pharmacists had no
 * queue.
 *
 * So an overridden row is tinted, tagged, and carries the registry's model
 * struck through beside the one actually running, with the reason and the
 * person who set it underneath, for as long as it is set. It is meant to look
 * like something you would want to finish.
 */

/** The step names, as somebody who does not read `registry.ts` would say them. */
const SEAT_LABEL: Readonly<Record<string, string>> = {
  reader: 'Leitor da receita',
  resolver: 'Resolução de insumos',
  explainer: 'Explicação das divergências',
  classifier: 'Triagem de anexos',
  orienter: 'Orientação da página',
  volume: 'Cálculo de volume',
  adversary: 'Revisão adversarial',
  strength: 'Dosagem no nome do produto',
}

/** How a trace names each step, which is how Langfuse groups the spend. */
const TRACE_LABEL: Readonly<Record<string, string>> = {
  'read-prescription': 'Leitor da receita',
  'resolve-ingredients': 'Resolução de insumos',
  'resolve-components': 'Resolução de componentes',
  'explain-findings': 'Explicação das divergências',
  'classify-document': 'Triagem de anexos',
  'derive-volumes': 'Cálculo de volume',
  'orient-page': 'Orientação da página',
  'adversarial-blind-read': 'Revisão adversarial — leitura própria',
  'adversarial-critique': 'Revisão adversarial — crítica',
}

const WINDOWS = [7, 30, 90] as const

const money = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'USD' })

const count = (value: number) => value.toLocaleString('pt-BR')

/** The model, without the routing segment nobody needs to read twice. */
const shortModel = (id: string) =>
  id.startsWith('openrouter/') ? id.slice('openrouter/'.length) : id

export function AiManagement({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<AiData | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [loading, setLoading] = useState(true)
  const [days, setDays] = useState<number>(30)
  const [models, setModels] = useState<string[] | null | undefined>()

  function load(window = days) {
    setLoading(true)
    void api
      .ai(window)
      .then((r) => {
        setData(r)
        setError(undefined)
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
      .finally(() => setLoading(false))
  }

  useEffect(() => load(days), [days])

  // Fetched once for the whole screen rather than per row: it is the same
  // four hundred models whichever step you are editing, and six copies of one
  // request is six chances for one of them to be the slow one.
  useEffect(() => {
    void api
      .aiModels()
      .then((r) => setModels(r.models))
      .catch(() => setModels(null))
  }, [])

  return (
    <div className="flex flex-col gap-6 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH2 text-text-strong">Gestão de IA</h1>
      </div>

      <Seats
        seats={data?.seats ?? []}
        models={models}
        loading={loading && data === undefined}
        error={error}
        onRetry={() => load()}
        onChanged={() => load()}
      />

      <Spend
        rows={data?.spend ?? null}
        available={data?.spendAvailable ?? false}
        days={days}
        onDays={setDays}
        loading={loading && data === undefined}
      />

      <AccountSpend days={days} />
    </div>
  )
}

function Seats({
  seats,
  models,
  loading,
  error,
  onRetry,
  onChanged,
}: {
  seats: readonly AiSeat[]
  models: string[] | null | undefined
  loading: boolean
  error: string | undefined
  onRetry: () => void
  onChanged: () => void
}) {
  /** The step being edited, if any. One at a time: a row is a decision. */
  const [editing, setEditing] = useState<string | undefined>()

  const columns: ReadonlyArray<Column<AiSeat>> = [
    {
      key: 'agent',
      header: 'Etapa',
      render: (seat) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-text-strong">{SEAT_LABEL[seat.agent] ?? seat.agent}</span>
          <code className="text-tSmall text-text-weak">{seat.agent}</code>
        </div>
      ),
    },
    {
      key: 'model',
      header: 'Modelo',
      render: (seat) =>
        editing === seat.agent ? (
          <EditSeat
            seat={seat}
            models={models}
            onCancel={() => setEditing(undefined)}
            onDone={() => {
              setEditing(undefined)
              onChanged()
            }}
          />
        ) : (
          <div className="flex flex-col gap-0.5">
            <code className="text-text-strong">{shortModel(seat.model)}</code>
            {seat.fallbacks.length > 0 && (
              /*
               * The chain, spelled out. "This step has a fallback" is the one
               * fact that turns a provider outage from an incident into a
               * slightly worse reading nobody has to be woken up for, and it
               * is invisible everywhere else.
               */
              <span
                className={`text-tSmall ${
                  seat.override?.fallbacks == null ? 'text-text-weak' : 'text-text-danger'
                }`}
              >
                depois: {seat.fallbacks.map(shortModel).join(' → ')}
              </span>
            )}
            {seat.override?.model != null && (
              /*
               * Struck through, not replaced, and only when the *model* moved.
               * An override that changed nothing but the chain leaves the step
               * on its registry model, and striking it through would say
               * otherwise. What the step should be on is half of what makes
               * this readable as a deviation -- and it is the value somebody
               * needs when they come to put it back.
               */
              <code className="text-tSmall text-text-weak line-through">
                {shortModel(seat.registryModel)}
              </code>
            )}
          </div>
        ),
    },
    {
      key: 'origin',
      header: 'Origem',
      render: (seat) =>
        seat.override === null ? (
          <Tag tone="neutral">registry</Tag>
        ) : (
          <div className="flex flex-col gap-1">
            <Tag tone="danger">
              {seat.override.model != null && seat.override.fallbacks != null
                ? 'modelo e fallback trocados'
                : seat.override.model != null
                  ? 'modelo trocado'
                  : 'fallback trocado'}
            </Tag>
            <span className="text-tSmall text-text-weak">
              {seat.override.setBy} · {new Date(seat.override.setAt).toLocaleString('pt-BR')}
            </span>
            <span className="text-tSmall text-text-weak">{seat.override.reason}</span>
          </div>
        ),
    },
    {
      key: 'actions',
      header: '',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (seat) =>
        editing === seat.agent ? null : (
          <div className="flex justify-end gap-2">
            <Button
              variant="tertiary"
              tone="neutral"
              size="small"
              onClick={() => setEditing(seat.agent)}
            >
              Trocar
            </Button>
            {seat.override !== null && (
              <Button
                variant="tertiary"
                tone="neutral"
                size="small"
                leadingIcon={icons.rotate}
                onClick={() => void api.clearSeatModel(seat.agent).then(onChanged)}
              >
                Voltar ao registry
              </Button>
            )}
          </div>
        ),
    },
  ]

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-tH3 text-text-strong">Modelos por etapa</h2>
      <p className="text-tSmall text-text-weak max-w-3xl">
        O modelo de cada etapa é decidido no <code>registry.ts</code>, com o golden set por trás.
        Trocar aqui é uma exceção — para quando um provedor parou de responder e esperar um deploy
        significa deixar a fila parada.
      </p>

      <DataTable
        columns={columns}
        rows={seats}
        rowKey={(seat) => seat.agent}
        loading={loading}
        error={error}
        onRetry={onRetry}
        pageSize={6}
        empty="Nenhuma etapa configurada."
        rowClassName={(seat) => (seat.override === null ? undefined : 'bg-surface-danger-weak')}
      />
    </section>
  )
}

/**
 * The inline editor: a model and a reason, in the row itself.
 *
 * In the row rather than in a dialog because the decision is a comparison --
 * this step is on that model and should be on this one - and a dialog covers
 * the thing being compared against.
 */
function EditSeat({
  seat,
  models,
  onCancel,
  onDone,
}: {
  seat: AiSeat
  models: string[] | null | undefined
  onCancel: () => void
  onDone: () => void
}) {
  const [model, setModel] = useState(seat.model)
  const [chain, setChain] = useState<string[]>([...seat.fallbacks])
  const [reason, setReason] = useState(seat.override?.reason ?? '')
  const [error, setError] = useState<string | undefined>()
  const [saving, setSaving] = useState(false)

  const modelMoved = model !== seat.model
  const chainMoved =
    chain.length !== seat.fallbacks.length || chain.some((id, at) => id !== seat.fallbacks[at])
  const ready = !saving && (modelMoved || chainMoved) && model.trim() !== '' && reason.trim() !== ''

  function save() {
    setSaving(true)
    /*
     * Only what moved is sent. An absent field means "leave the registry's
     * answer alone", so sending both every time would turn "I added a
     * fallback" into an override of the model as well -- and the screen would
     * then show a step as deviating over a value nobody chose to change.
     */
    void api
      .setSeatModel(
        seat.agent,
        {
          ...(modelMoved ? { model: model.trim() } : {}),
          ...(chainMoved ? { fallbacks: chain } : {}),
        },
        reason.trim(),
      )
      .then(onDone)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'não foi possível trocar'),
      )
      .finally(() => setSaving(false))
  }

  /** Models not already spoken for, so a chain cannot repeat itself. */
  const available = (except: string) =>
    (models ?? []).filter((id) => id === except || (id !== model && !chain.includes(id)))

  return (
    <div className="flex min-w-[24rem] flex-col gap-3">
      <div className="flex flex-col gap-1">
        <span className="text-tTiny text-text-weak font-semibold uppercase">Modelo</span>
        {models === undefined ? (
          <p className="text-tSmall text-text-weak">Carregando modelos…</p>
        ) : models === null ? (
          /*
           * Free text when the catalogue cannot be reached, because the outage
           * that brings somebody here may be the reason it cannot. Refusing the
           * lever because the inventory is down would be exactly backwards.
           */
          <Input
            size="small"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Catálogo indisponível — digite o id do modelo"
          />
        ) : (
          <SearchableSelect options={available(model)} value={model} onChange={setModel} />
        )}
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-tTiny text-text-weak font-semibold uppercase">
          Fallback, em ordem
        </span>
        <p className="text-tSmall text-text-weak">
          Tentados na ordem se o modelo acima falhar — sem crédito, fora do ar, limite de taxa. Não
          muda nada quando a chamada dá certo.
        </p>

        {chain.map((id, at) => (
          <div key={`${id}:${at}`} className="flex items-center gap-2">
            <span className="text-tSmall text-text-weak w-4 tabular-nums">{at + 1}.</span>
            <div className="flex-1">
              {models === null ? (
                <Input
                  size="small"
                  value={id}
                  onChange={(e) =>
                    setChain(chain.map((old, i) => (i === at ? e.target.value : old)))
                  }
                />
              ) : (
                <SearchableSelect
                  options={available(id)}
                  value={id}
                  onChange={(next) => setChain(chain.map((old, i) => (i === at ? next : old)))}
                />
              )}
            </div>
            <Button
              variant="tertiary"
              tone="danger"
              size="icon"
              aria-label={`Remover fallback ${at + 1}`}
              leadingIcon={icons.close}
              onClick={() => setChain(chain.filter((_, i) => i !== at))}
            />
          </div>
        ))}

        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          onClick={() => setChain([...chain, ''])}
          disabled={chain.includes('')}
        >
          Adicionar fallback
        </Button>
      </div>

      <Input
        size="small"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Motivo — fica visível enquanto a troca durar"
      />

      {error !== undefined && <p className="text-tSmall text-text-danger">{error}</p>}

      <div className="flex gap-2">
        <Button variant="primary" tone="brand" size="small" disabled={!ready} onClick={save}>
          Salvar
        </Button>
        <Button variant="tertiary" tone="neutral" size="small" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  )
}

function Spend({
  rows,
  available,
  days,
  onDays,
  loading,
}: {
  rows: readonly AiSpendRow[] | null
  available: boolean
  days: number
  onDays: (days: number) => void
  loading: boolean
}) {
  const total = (rows ?? []).reduce((sum, row) => sum + row.cost, 0)

  const columns: ReadonlyArray<Column<AiSpendRow>> = [
    {
      key: 'model',
      header: 'Modelo',
      render: (row) => <code className="text-text-strong">{row.model}</code>,
    },
    {
      key: 'agent',
      header: 'Etapa',
      render: (row) =>
        row.agent === null ? (
          // A call with no step behind it. Worth showing rather than hiding:
          // it is usually a benchmark or an eval, and it is spending the same
          // money.
          <span className="text-text-weak">fora de etapa</span>
        ) : (
          (TRACE_LABEL[row.agent] ?? row.agent)
        ),
    },
    {
      key: 'calls',
      header: 'Chamadas',
      headerClassName: 'text-right',
      className: 'text-right tabular-nums',
      render: (row) => count(row.calls),
    },
    {
      key: 'tokens',
      header: 'Tokens',
      headerClassName: 'text-right',
      className: 'text-right tabular-nums',
      render: (row) => count(row.tokens),
    },
    {
      key: 'cost',
      header: 'Custo',
      headerClassName: 'text-right',
      className: 'text-right tabular-nums',
      render: (row) => money(row.cost),
    },
    {
      key: 'each',
      header: 'Por chamada',
      headerClassName: 'text-right',
      className: 'text-right tabular-nums',
      render: (row) => (row.calls === 0 ? '—' : money(row.cost / row.calls)),
    },
  ]

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-tH3 text-text-strong">Custo por modelo</h2>
        <div className="flex gap-1">
          {WINDOWS.map((window) => (
            <Button
              key={window}
              variant={window === days ? 'secondary' : 'tertiary'}
              tone="neutral"
              size="small"
              onClick={() => onDays(window)}
            >
              {window} dias
            </Button>
          ))}
        </div>
      </div>

      {!loading && !available ? (
        /*
         * The one thing this screen must never do is render a confident zero.
         * Langfuse ingestion was suspended on a usage threshold for days and
         * nothing in the product noticed -- an empty table would have looked
         * exactly like a quiet week.
         */
        <p className="text-tSmall text-text-weak rounded-base border-stroke-weak bg-background-raised border p-4">
          Sem dados de custo: o Langfuse não respondeu ou não está configurado. Isso não quer dizer
          que nada foi gasto — quer dizer que ninguém está medindo.
        </p>
      ) : (
        <DataTable
          columns={columns}
          rows={rows ?? []}
          rowKey={(row) => `${row.model}:${row.agent ?? '—'}`}
          loading={loading}
          pageSize={8}
          empty={`Nenhuma chamada nos últimos ${days} dias.`}
          footer={
            (rows ?? []).length === 0 ? undefined : (
              <div className="text-tSmall text-text-strong flex justify-between px-4 py-2">
                <span>Total</span>
                <span className="tabular-nums">{money(total)}</span>
              </div>
            )
          }
        />
      )}
    </section>
  )
}
